import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  ShoppingCart, Bot, Globe, Zap, Package, Palette, Repeat, CreditCard,
  Truck, BarChart3, Layers, Code, Monitor, PenTool, Settings, Wrench,
  MessageSquare, BookOpen, Brain, Sparkles, ArrowRight, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

/* ─── Data ─── */

const PILLARS = [
  {
    id: "ecommerce",
    badge: "ECOMMERCE",
    badge_ar: "التجارة الإلكترونية",
    title: "Build, Launch & Scale",
    title_ar: "أنشئ، أطلق ووسّع",
    highlight: "Online Stores",
    highlight_ar: "متجرك الإلكتروني",
    description:
      "End-to-end ecommerce, from custom store builds and migrations to checkout optimization, payment integrations, and subscription commerce. We help brands sell smarter and scale faster on Shopify and WooCommerce.",
    description_ar:
      "تجارة إلكترونية شاملة, من بناء المتاجر المخصصة والترحيل إلى تحسين الدفع والتكاملات والاشتراكات. نساعد العلامات التجارية على البيع بذكاء والتوسع بسرعة على Shopify وWooCommerce.",
    cta_link: "/ecommerce",
    icon: ShoppingCart,
    services: [
      { icon: ShoppingCart, title: "Shopify Store Design & Development", title_ar: "تصميم وتطوير متاجر Shopify", desc: "Conversion-focused Shopify stores built for speed and growth.", desc_ar: "متاجر Shopify مصممة للتحويل والسرعة والنمو.", link: "/services/shopify-store-design" },
      { icon: Zap, title: "Shopify Plus Solutions", title_ar: "حلول Shopify Plus", desc: "Enterprise-grade ecommerce with custom checkouts and automations.", desc_ar: "تجارة إلكترونية مؤسسية مع صفحات دفع مخصصة وأتمتة.", link: "/services/shopify-plus" },
      { icon: Package, title: "WooCommerce Development", title_ar: "تطوير WooCommerce", desc: "Flexible, open-source stores on WordPress for unique requirements.", desc_ar: "متاجر مفتوحة المصدر ومرنة على WordPress.", link: "/services/woocommerce-development" },
      { icon: Palette, title: "Ecommerce UX Optimization", title_ar: "تحسين تجربة المستخدم", desc: "Data-driven design that reduces friction and increases conversions.", desc_ar: "تصميم مبني على البيانات يقلل الاحتكاك ويزيد التحويلات.", link: "/services/ecommerce-ux-optimization" },
      { icon: Repeat, title: "Store Migration", title_ar: "ترحيل المتاجر", desc: "Seamless migrations preserving SEO, data, and design integrity.", desc_ar: "ترحيل سلس مع الحفاظ على SEO والبيانات والتصميم.", link: "/services/store-migration" },
      { icon: CreditCard, title: "Payment Integrations", title_ar: "تكامل بوابات الدفع", desc: "Local and global payment gateways for a seamless checkout.", desc_ar: "بوابات دفع محلية وعالمية لتجربة سلسة.", link: "/services/payment-integrations" },
      { icon: Truck, title: "Logistics & Fulfillment", title_ar: "الخدمات اللوجستية", desc: "Shipping, warehousing, and fulfillment integrations.", desc_ar: "تكامل الشحن والتخزين والتنفيذ.", link: "/services/logistics-fulfillment" },
      { icon: BarChart3, title: "Analytics & Growth", title_ar: "التحليلات والنمو", desc: "Track performance and optimize continuously for revenue.", desc_ar: "تتبع الأداء وتحسين مستمر للإيرادات.", link: "/services/ecommerce-analytics" },
    ],
  },
  {
    id: "web",
    badge: "WEB",
    badge_ar: "الويب",
    title: "High-Performance",
    title_ar: "مواقع احترافية",
    highlight: "Websites",
    highlight_ar: "عالية الأداء",
    description:
      "Fast, beautiful, and scalable websites on Webflow and WordPress. Corporate sites, marketing landing pages, multilingual experiences, pixel-perfect results that drive business outcomes.",
    description_ar:
      "مواقع سريعة وجميلة وقابلة للتوسع على Webflow وWordPress. مواقع مؤسسية وصفحات هبوط تسويقية وتجارب متعددة اللغات, نتائج مثالية تحقق أهداف الأعمال.",
    cta_link: "/web",
    icon: Globe,
    services: [
      { icon: Layers, title: "Webflow Development", title_ar: "تطوير مواقع Webflow", desc: "Visual-first, responsive sites, fast and easy to manage.", desc_ar: "مواقع بصرية متجاوبة, سريعة وسهلة الإدارة.", link: "/services/webflow-development" },
      { icon: Code, title: "WordPress Development", title_ar: "تطوير WordPress", desc: "Custom WordPress builds for performance and flexibility.", desc_ar: "مواقع WordPress مخصصة للأداء والمرونة.", link: "/services/wordpress-development" },
      { icon: Monitor, title: "Corporate Websites", title_ar: "المواقع المؤسسية", desc: "Professional sites that communicate your brand story.", desc_ar: "مواقع احترافية تنقل قصة علامتك التجارية.", link: "/services/corporate-websites" },
      { icon: PenTool, title: "Landing Pages", title_ar: "صفحات الهبوط", desc: "High-converting pages for campaigns and product launches.", desc_ar: "صفحات عالية التحويل للحملات وإطلاق المنتجات.", link: "/services/landing-pages" },
      { icon: Globe, title: "Multilingual Websites", title_ar: "المواقع متعددة اللغات", desc: "Fully localized, RTL-ready multilingual experiences.", desc_ar: "تجارب متعددة اللغات مترجمة بالكامل وجاهزة لـ RTL.", link: "/services/multilingual-websites" },
      { icon: Settings, title: "CMS Implementation", title_ar: "أنظمة إدارة المحتوى", desc: "Structured content management for team independence.", desc_ar: "إدارة محتوى منظمة لاستقلالية الفريق.", link: "/services/cms-implementation" },
      { icon: Wrench, title: "Redesign & Optimization", title_ar: "إعادة تصميم وتحسين", desc: "Modernize with better design, speed, and SEO.", desc_ar: "حدّث بتصميم محسّن وسرعة أفضل وSEO.", link: "/services/website-redesign" },
    ],
  },
  {
    id: "ai",
    badge: "AI",
    badge_ar: "الذكاء الاصطناعي",
    title: "AI-Powered",
    title_ar: "حلول مدعومة",
    highlight: "Automation",
    highlight_ar: "بالذكاء الاصطناعي",
    description:
      "Practical AI that saves time and drives results, from workflow automation and content generation to custom agents, chatbots, and intelligent internal tools.",
    description_ar:
      "ذكاء اصطناعي عملي يوفر الوقت ويحقق نتائج, من أتمتة سير العمل وإنشاء المحتوى إلى الوكلاء المخصصين وروبوتات الدردشة والأدوات الداخلية الذكية.",
    cta_link: "/ai",
    icon: Bot,
    services: [
      { icon: Bot, title: "AI Agents & Automation", title_ar: "وكلاء الذكاء الاصطناعي", desc: "Autonomous agents for customer interactions and operations.", desc_ar: "وكلاء مستقلون لتفاعلات العملاء والعمليات.", link: "/services/ai-agents" },
      { icon: Sparkles, title: "AI Content Generation", title_ar: "إنشاء المحتوى بالذكاء الاصطناعي", desc: "Brand-consistent copy, blog posts, and product descriptions at scale.", desc_ar: "محتوى متسق مع العلامة التجارية على نطاق واسع.", link: "/services/ai-content-generation" },
      { icon: MessageSquare, title: "Chatbots & Assistants", title_ar: "روبوتات الدردشة", desc: "Smart chatbots that understand your business and guide customers.", desc_ar: "روبوتات ذكية تفهم عملك وتوجه العملاء.", link: "/services/chatbots-assistants" },
      { icon: Settings, title: "Workflow Integrations", title_ar: "تكامل سير العمل", desc: "Connect AI to your CRM, ecommerce, and productivity tools.", desc_ar: "ربط الذكاء الاصطناعي بأدواتك الحالية.", link: "/services/workflow-integrations" },
      { icon: BookOpen, title: "Knowledge Base Automation", title_ar: "أتمتة قواعد المعرفة", desc: "AI-powered knowledge bases that serve instant answers.", desc_ar: "قواعد معرفة ذكية تقدم الإجابات فوراً.", link: "/services/knowledge-base-automation" },
      { icon: Brain, title: "Custom AI Tools", title_ar: "أدوات ذكية مخصصة", desc: "Internal dashboards and tools powered by AI analytics.", desc_ar: "لوحات معلومات وأدوات داخلية مدعومة بالذكاء الاصطناعي.", link: "/services/custom-ai-tools" },
    ],
  },
];

/* ─── Animations ─── */

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

/* ─── Component ─── */

const Services = () => {
  const { lang, localePath, t } = useLanguage();
  const isAr = lang === "ar";
  const { canonical, hreflang } = buildHreflang("/services");
  useCmsPageSEO({
    title: isAr ? "خدمات الويب والتجارة الإلكترونية في الكويت | وافاي" : "Web & eCommerce Services in Kuwait | Wavai",
    description: isAr
      ? "استكشف باقة خدمات وافاي الكاملة في الكويت: شوبيفاي، ووردبريس، ووكومرس، ويبفلو، الذكاء الاصطناعي، حلول Lovable، الاستضافة والنمو الرقمي."
      : "Explore Wavai's full range of services in Kuwait: Shopify, WordPress, WooCommerce, Webflow, AI, Lovable builds, hosting and digital growth.",
    keywords: "web services Kuwait, ecommerce services Kuwait, Shopify Kuwait, AI services Kuwait",
    canonical,
    hreflang,
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* ── Hero ── */}
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero opacity-30" />
          {/* Decorative dots */}
          <div className="absolute top-1/2 -translate-y-1/2 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-primary/5 rounded-full blur-3xl" />

          <div className="container px-6 relative z-10">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            >
              <motion.p
                className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-5"
                variants={fadeUp}
              >
                {t("Our Services", "خدماتنا")}
              </motion.p>
              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-6"
                variants={fadeUp}
              >
                {t("Kuwait's Full-Service", "وكالة الكويت الشاملة لـ")}
                <span className="text-gradient"> {t("Web, eCommerce & AI Agency", "الويب والتجارة الإلكترونية والذكاء الاصطناعي")}</span>
              </motion.h1>
              <motion.p
                className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed"
                variants={fadeUp}
              >
                {t(
                  "From custom WordPress builds to Shopify stores, Webflow sites and AI-powered apps, Wavai is Kuwait's one-stop studio for ambitious digital projects. Pick the service — we'll handle the rest.",
                  "من مواقع ووردبريس المخصّصة إلى متاجر شوبيفاي ومواقع ويبفلو وتطبيقات الذكاء الاصطناعي، وافاي ستوديو الكويت الشامل للمشاريع الرقمية الطموحة. اختر الخدمة، ونحن نتولى الباقي."
                )}
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* ── Pillar Sections ── */}
        {PILLARS.map((pillar, idx) => (
          <section
            key={pillar.id}
            className={`py-12 lg:py-32 relative ${idx % 2 === 1 ? "bg-secondary/30" : ""}`}
          >
            {/* Subtle top divider */}
            {idx > 0 && <div className="absolute top-0 inset-x-0 h-px bg-border" />}

            <div className="container px-6">
              {/* Section Header — left-aligned, editorial style */}
              <motion.div
                className="max-w-3xl mb-16"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
              >
                <motion.div className="flex items-center gap-3 mb-5" variants={fadeUp}>
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <pillar.icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                    {isAr ? pillar.badge_ar : pillar.badge}
                  </span>
                </motion.div>

                <motion.h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-[1.1] mb-5"
                  variants={fadeUp}
                >
                  {isAr ? pillar.title_ar : pillar.title}{" "}
                  <span className="text-gradient">{isAr ? pillar.highlight_ar : pillar.highlight}</span>
                </motion.h2>

                <motion.p
                  className="text-lg text-muted-foreground leading-relaxed max-w-2xl"
                  variants={fadeUp}
                >
                  {isAr ? pillar.description_ar : pillar.description}
                </motion.p>
              </motion.div>

              {/* Service Cards Grid */}
              <motion.div
                className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
              >
                {pillar.services.map((service) => {
                  const inner = (
                    <>
                      <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center mb-5 group-hover:bg-primary/15 transition-colors duration-300">
                        <service.icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="text-base font-sans font-medium mb-2 group-hover:text-primary transition-colors duration-300 leading-snug">
                        {isAr ? service.title_ar : service.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {isAr ? service.desc_ar : service.desc}
                      </p>
                      {service.link && (
                        <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-primary opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                          {t("Learn more", "اعرف المزيد")} <ArrowRight className="h-3 w-3" />
                        </div>
                      )}
                    </>
                  );

                  const cardClasses =
                    "group relative p-6 rounded-2xl bg-card border border-border/60 hover:border-primary/25 transition-all duration-300 hover:shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.12)] hover:-translate-y-0.5 flex flex-col";

                  if (service.link) {
                    return (
                      <motion.div key={service.title} variants={fadeUp}>
                        <Link to={localePath(service.link)} className={cardClasses + " h-full"}>
                          {inner}
                        </Link>
                      </motion.div>
                    );
                  }

                  return (
                    <motion.div key={service.title} variants={fadeUp} className={cardClasses}>
                      {inner}
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Section CTA */}
              <motion.div
                className="mt-10"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <Link
                  to={localePath(pillar.cta_link)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link"
                >
                  {t(
                    `Explore ${pillar.badge.toLowerCase()} services`,
                    `استكشف خدمات ${pillar.badge_ar}`
                  )}
                  <ArrowRight className="h-4 w-4 group-hover/link:translate-x-0.5 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </section>
        ))}

        {/* ── Bottom CTA ── */}
        <section className="py-12 lg:py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero opacity-20" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />

          <div className="container px-6 relative z-10">
            <motion.div
              className="max-w-2xl mx-auto text-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            >
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-[1.1] mb-5"
                variants={fadeUp}
              >
                {t("Ready to", "هل أنت مستعد")}{" "}
                <span className="text-gradient">{t("Get Started?", "للبدء؟")}</span>
              </motion.h2>
              <motion.p
                className="text-lg text-muted-foreground mb-10 max-w-lg mx-auto leading-relaxed"
                variants={fadeUp}
              >
                {t(
                  "Tell us about your project and we'll craft a tailored plan to bring your vision to life.",
                  "أخبرنا عن مشروعك وسنضع خطة مخصصة لتحقيق رؤيتك."
                )}
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
                variants={fadeUp}
              >
                <Button asChild size="lg" className="min-w-[180px]">
                  <Link to={localePath("/start-a-project")}>
                    {t("Start a Project", "ابدأ مشروعك")}
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-w-[180px]">
                  <Link to={localePath("/work")}>
                    {t("View Our Work", "شاهد أعمالنا")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Services;
